import React, { useEffect, useRef, useState } from 'react';
import Header from '../Header/Header';
import PublicListPromoBar from '../PublicListPromoBar';
import StockTable, { Statistic, CategoryHeader } from './StockTable';
import StockManagementControls from './StockManagementControls';
import { TickerDisplayData } from '../../types/ticker';
import { UserTickerList } from '../../services/userTickerListApi';
import { useTheme } from '../../contexts/ThemeContext';

export interface StockTableLayoutProps {
  // Data
  tickerList: string[];
  tickerData: TickerDisplayData[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  
  // UI State
  isEditingTickers: boolean;
  hiddenStatistics: string[];
  
  // Drag and Drop State
  draggedTicker: string | null;
  dragOverTicker: string | null;
  droppedTicker: string | null;
  
  // Statistics Configuration
  statistics: Statistic[];
  categoryHeaders?: CategoryHeader[];
  getTickerData: (symbol: string) => TickerDisplayData | undefined;
  formatStatisticValue: (statisticKey: string, ticker: TickerDisplayData) => React.ReactNode;
  getStatisticColor: (statisticKey: string, ticker: TickerDisplayData) => string;
  
  // Pro-only locked statistics
  lockedStatistics?: string[];
  
  // Pro-only statistics that should always show PRO tag (regardless of subscription status)
  proOnlyStatistics?: string[];
  
  // Locked tickers (beyond free tier limit - e.g., beyond 10th position for free users)
  lockedTickers?: string[];
  
  // Event Handlers
  onRefetch: () => void;
  onTickerClick: (tickerSymbol: string) => void;
  onRemoveTicker: (ticker: string) => void;
  onDragStart: (e: React.DragEvent, ticker: string) => void;
  onDragOver: (e: React.DragEvent, ticker: string) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, ticker: string) => void;
  onDragEnd: () => void;
  onHideStatistic: (statisticKey: string) => void;
  onShowStatistic: (statisticKey: string) => void;
  onCategoryInfoClick?: (categoryName: string) => void;
  onIndicatorInfoClick?: (indicatorKey: string) => void;
  indicatorKeysWithInfo?: string[]; // Optional: if provided, only show info buttons for these keys. If not provided, show for all when onIndicatorInfoClick is provided.
  
  // Stock Management Controls
  listName: string;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onDoneEdit: () => void;
  onAddTicker: (ticker: string) => void;
  onSave: () => void;
  tempListName?: string;
  onTempListNameChange?: (name: string) => void;
  
  // User List Management (when logged in)
  userLists?: UserTickerList[];
  currentListId?: number;
  onListChange?: (listId: number) => void;
  onCreateNewList?: () => void;
  onCreateNewListWithName?: (listName: string) => Promise<void>;
  
  // Delete list functionality
  onDeleteList?: () => void;
  showDeleteButton?: boolean;
  
  // Share list functionality
  onShareList?: () => void;
  showShareButton?: boolean;
  
  // Optional: Refresh usage limits callback
  onRefreshUsageLimits?: (refreshFn: () => void) => void;
  
  // Tabs (customizable)
  tabs: React.ReactNode;
  
  // Optional timeframe toggle (for Fundamentals page)
  timeframeToggle?: React.ReactNode;
  
  // UI Display Options
  showRefreshButton?: boolean;
  showDelayedPricesText?: boolean;
  showLastUpdatedDate?: boolean; // If true, show date and time; if false, show only time
  
  // Custom ticker column width (defaults to 150px)
  tickerColumnWidth?: string;
  
  // Multiplier to reduce statistics column width (defaults to 1.0, use < 1.0 to make it narrower)
  statisticsColumnWidthMultiplier?: number;
  
  // Multiplier for full screen mode statistics column width (defaults to statisticsColumnWidthMultiplier if not provided)
  fullScreenStatisticsColumnWidthMultiplier?: number;

  /** On small screens, use the same Indicators column width as ListDaily. */
  useListDailySmallScreenColumnWidth?: boolean;
  
  // Optional: Public list mode (read-only)
  isPublicList?: boolean;
  
  // Optional: Hide header (for landing page)
  hideHeader?: boolean;
  
  // Optional: Custom max height for scrollable container
  maxHeight?: string;
  
  // Optional: Show promotional tooltip for new users from Get Started page
  showPromotionalTooltip?: boolean;

  // Optional: Show promotional bar for unauthenticated viewers of public lists
  showPublicListPromoBar?: boolean;

  /** Passed to StockTable: extra top padding on Pros/Cons rows (ListProsCons). */
  prosConsComfortablePadding?: boolean;
}

const StockTableLayout: React.FC<StockTableLayoutProps> = ({
  tickerList,
  tickerData,
  loading,
  error,
  lastUpdated,
  isEditingTickers,
  hiddenStatistics,
  draggedTicker,
  dragOverTicker,
  droppedTicker,
  statistics,
  categoryHeaders,
  getTickerData,
  formatStatisticValue,
  getStatisticColor,
  lockedStatistics = [],
  proOnlyStatistics = [],
  lockedTickers = [],
  onRefetch,
  onTickerClick,
  onRemoveTicker,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onHideStatistic,
  onShowStatistic,
  onCategoryInfoClick,
  onIndicatorInfoClick,
  indicatorKeysWithInfo,
  listName,
  onStartEdit,
  onCancelEdit,
  onDoneEdit,
  onAddTicker,
  onSave,
  tempListName,
  onTempListNameChange,
  userLists,
  currentListId,
  onListChange,
  onCreateNewList,
  onCreateNewListWithName,
  onDeleteList,
  showDeleteButton = false,
  onShareList,
  showShareButton = false,
  onRefreshUsageLimits,
  tabs,
  timeframeToggle,
  showRefreshButton = true,
  showDelayedPricesText = true,
  showLastUpdatedDate = false,
  isPublicList = false,
  hideHeader = false,
  maxHeight,
  tickerColumnWidth,
  statisticsColumnWidthMultiplier,
  fullScreenStatisticsColumnWidthMultiplier,
  useListDailySmallScreenColumnWidth,
  showPromotionalTooltip = false,
  showPublicListPromoBar = false,
  prosConsComfortablePadding = false,
}) => {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Handle theme transitions to prevent flash
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Remove the theme-transition-complete class to disable transitions
    container.classList.remove('theme-transition-complete');
    
    // Use requestAnimationFrame to ensure the class removal happens before the theme change
    const timeoutId = setTimeout(() => {
      // Re-enable transitions after a short delay
      container.classList.add('theme-transition-complete');
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [theme]);

  return (
    <div ref={containerRef} className="flex flex-col flex-grow bg-background-light dark:bg-background">
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes dropIn {
            0% {
              transform: scale(0.8) translateY(-10px);
              opacity: 0.7;
              box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.4);
            }
            50% {
              transform: scale(1.05) translateY(0);
              opacity: 1;
              box-shadow: 0 0 20px 5px rgba(251, 191, 36, 0.3);
            }
            100% {
              transform: scale(1) translateY(0);
              opacity: 1;
              box-shadow: 0 0 0 0 rgba(251, 191, 36, 0);
            }
          }
          .drop-in-animation {
            animation: dropIn 0.6s ease-out;
          }
          
          /* Prevent theme transition flash - disable transitions during theme changes */
          * {
            transition: none !important;
          }
          
          /* Re-enable transitions after theme change is complete */
          .theme-transition-complete * {
            transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease !important;
          }
          
          /* Ensure smooth theme transitions for ticker cells after theme change */
          .theme-transition-complete th, 
          .theme-transition-complete td {
            transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease !important;
          }
          
          /* Ensure sticky header has solid background to prevent content showing through */
          table thead.sticky-header-border {
            background-color: rgb(249, 250, 251) !important;
          }
          
          .dark table thead.sticky-header-border {
            background-color: rgb(17, 24, 39) !important;
          }
          
          /* Sticky header bottom border - using a class-based approach */
          table thead.sticky-header-border::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 1px;
            background-color: rgb(229, 231, 235);
            z-index: 51;
            pointer-events: none;
          }
          
          .dark table thead.sticky-header-border::after {
            background-color: rgb(55, 65, 81);
          }
          
          /* Alternative: target thead with position sticky using attribute selector */
          table thead[style*="sticky"]::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 1px;
            background-color: rgb(229, 231, 235);
            z-index: 51;
            pointer-events: none;
          }
          
          .dark table thead[style*="sticky"]::after {
            background-color: rgb(55, 65, 81);
          }
          
          /* Sticky "Last Closing Price" row bottom border - using box-shadow, only when stuck */
          table tbody tr.sticky-last-closing-price.is-stuck {
            box-shadow: 0 1px 0 0 rgb(229, 231, 235) !important;
          }
          
          .dark table tbody tr.sticky-last-closing-price.is-stuck {
            box-shadow: 0 1px 0 0 rgb(55, 65, 81) !important;
          }
          
          /* Fallback: always show border when row has sticky class (for debugging) */
          /* Uncomment below to test if CSS is working */
          /*
          table tbody tr.sticky-last-closing-price {
            box-shadow: 0 1px 0 0 rgb(229, 231, 235) !important;
          }
          
          .dark table tbody tr.sticky-last-closing-price {
            box-shadow: 0 1px 0 0 rgb(55, 65, 81) !important;
          }
          */
        `
      }} />
      {!hideHeader && !isFullScreen && <Header sticky={false} />}
      {!hideHeader && !isFullScreen && showPublicListPromoBar && <PublicListPromoBar />}

      <main className="px-1 lg:px-2 xl:px-4 pt-4 sm:pt-8 pb-8 sm:pb-6">
        <div className="bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md shadow-lg overflow-hidden">
          {/* Top controls */}
          <div className="p-4 flex justify-between items-center">
            <StockManagementControls
              listName={listName}
              isEditingTickers={isEditingTickers}
              onStartEdit={onStartEdit}
              onCancelEdit={onCancelEdit}
              onDoneEdit={onDoneEdit}
              tickerList={tickerList}
              onAddTicker={onAddTicker}
              onSave={onSave}
              tempListName={tempListName}
              onTempListNameChange={onTempListNameChange}
              userLists={userLists}
              currentListId={currentListId}
              onListChange={onListChange}
              onCreateNewList={onCreateNewList}
              onCreateNewListWithName={onCreateNewListWithName}
              onDeleteList={onDeleteList}
              showDeleteButton={showDeleteButton}
              onShareList={onShareList}
              showShareButton={showShareButton}
              onRefreshUsageLimits={onRefreshUsageLimits}
              isPublicList={isPublicList}
              showPromotionalTooltip={showPromotionalTooltip}
            />
          </div>

          {/* Tabs */}
          <div className="border-b border-border-light dark:border-border-color rounded-none">
            <nav className="px-4">
              {tabs}
            </nav>
          </div>

          {/* Timeframe Toggle (optional) */}
          {timeframeToggle && (
            <div className="px-4 py-3 border-t border-b border-gray-200 dark:border-border-color">
              {timeframeToggle}
            </div>
          )}

          {/* Stock Table */}
          {(() => {
            console.log('🟢 [StockTableLayout] Passing hiddenStatistics to StockTable:', hiddenStatistics, 'length:', hiddenStatistics.length);
            return null;
          })()}
          <StockTable
            tickerList={tickerList}
            tickerData={tickerData}
            loading={loading}
            error={error}
            lastUpdated={lastUpdated}
            isEditingTickers={isEditingTickers}
            hiddenStatistics={hiddenStatistics}
            draggedTicker={draggedTicker}
            dragOverTicker={dragOverTicker}
            droppedTicker={droppedTicker}
            statistics={statistics}
            categoryHeaders={categoryHeaders}
            getTickerData={getTickerData}
            formatStatisticValue={formatStatisticValue}
            getStatisticColor={getStatisticColor}
            lockedStatistics={lockedStatistics}
            proOnlyStatistics={proOnlyStatistics}
            lockedTickers={lockedTickers}
            onRefetch={onRefetch}
            onTickerClick={onTickerClick}
            onRemoveTicker={onRemoveTicker}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            onHideStatistic={onHideStatistic}
            onShowStatistic={onShowStatistic}
            onCategoryInfoClick={onCategoryInfoClick}
            onIndicatorInfoClick={onIndicatorInfoClick}
            indicatorKeysWithInfo={indicatorKeysWithInfo}
            showRefreshButton={showRefreshButton}
            showDelayedPricesText={showDelayedPricesText}
            showLastUpdatedDate={showLastUpdatedDate}
            onFullScreenChange={setIsFullScreen}
            maxHeight={maxHeight}
            tickerColumnWidth={tickerColumnWidth}
            statisticsColumnWidthMultiplier={statisticsColumnWidthMultiplier}
            fullScreenStatisticsColumnWidthMultiplier={fullScreenStatisticsColumnWidthMultiplier}
            useListDailySmallScreenColumnWidth={useListDailySmallScreenColumnWidth}
            isPublicList={isPublicList}
            prosConsComfortablePadding={prosConsComfortablePadding}
          />
        </div>
      </main>
    </div>
  );
};

export default StockTableLayout;
